import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { PendingSubscriptionInterface } from "../../common/interfaces";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";

// Marca que o aviso automático desta sessão de login já foi dado. Só controla a
// abertura automática do modal — a busca continua acontecendo a cada carga da
// página, senão o aviso da barra superior sumiria depois de um F5.
const SESSION_FLAG = "pendingPaymentChecked";

const PROFILES_WITH_NOTICE = ["ADMIN", "OWNER", "MANAGER"];

type PendingPaymentContextValue = {
  pendings: PendingSubscriptionInterface[];
  /** Alguma mensalidade já passou da data de vencimento. */
  hasOverdue: boolean;
  total: number;
  companyCount: number;
  modalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
};

const EMPTY: PendingPaymentContextValue = {
  pendings: [],
  hasOverdue: false,
  total: 0,
  companyCount: 0,
  modalOpen: false,
  openModal: () => {},
  closeModal: () => {}
};

const PendingPaymentContext = createContext<PendingPaymentContextValue>(EMPTY);

/** Seguro fora do provider: devolve o estado vazio em vez de estourar. */
export const usePendingPayment = () => useContext(PendingPaymentContext);

export const PendingPaymentProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { userProfile, isAuthChecked } = useAuth();
  const [pendings, setPendings] = useState<PendingSubscriptionInterface[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!isAuthChecked || !userProfile) return;
    if (!PROFILES_WITH_NOTICE.includes(userProfile)) return;

    let active = true;
    const check = async () => {
      try {
        const { data } = await api.get<PendingSubscriptionInterface[]>(
          "/subscription/mine/pending"
        );
        if (!active) return;

        const items = Array.isArray(data) ? data : [];
        setPendings(items);

        const overdue = items.some((p) => Number(p.days_overdue ?? 0) > 0);
        const alreadyWarned = !!sessionStorage.getItem(SESSION_FLAG);

        // Só a mensalidade JÁ VENCIDA interrompe o login com o modal, e
        // uma vez por sessão. A que ainda está no prazo fica apenas no
        // aviso da barra superior, que o usuário abre quando quiser.
        if (overdue && !alreadyWarned && userProfile !== "ADMIN") {
          setModalOpen(true);
        }
        if (items.length > 0) sessionStorage.setItem(SESSION_FLAG, "1");
      } catch (error: any) {
        // Endpoint ainda não disponível no back (404) ou outro erro: falha
        // silenciosa para não incomodar o usuário a cada login.
        if (error.response?.status !== 404) {
          console.error(
            "Erro ao checar mensalidades em aberto:",
            error.response?.data || error
          );
        }
      }
    };

    check();
    return () => {
      active = false;
    };
  }, [isAuthChecked, userProfile]);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const value = useMemo<PendingPaymentContextValue>(
    () => ({
      pendings,
      hasOverdue: pendings.some((p) => Number(p.days_overdue ?? 0) > 0),
      total: pendings.reduce((sum, p) => sum + Number(p.charged_amount || 0), 0),
      companyCount: new Set(pendings.map((p) => p.company_id)).size,
      modalOpen,
      openModal,
      closeModal
    }),
    [pendings, modalOpen, openModal, closeModal]
  );

  return (
    <PendingPaymentContext.Provider value={value}>
      {children}
    </PendingPaymentContext.Provider>
  );
};
